// BandBreakdown — the "bands" section type of the Analysis panel (US-917 ·
// R-1/R-2/R-5). One outlined card per area band (Urban / Rural / Extreme
// rural) with plain label/value stats, bar rows (LinearProgress colored by
// token), and an optional totals footer. Population and Coverage Gap are both
// built on this shape by config/analysisConfig.js — this component renders
// whatever props the config derived and never computes domain values itself.
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

const clampPercent = (value) =>
  Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : 0;

// One bar row: optional label (technology), optional detail (count / total
// unit), optional value text (percent), and the progress bar itself.
const BandBar = ({ label, detail, valueText, percent, color }) => (
  <Box>
    {(label || detail || valueText) && (
      <Stack
        direction="row"
        spacing={1}
        alignItems="baseline"
        justifyContent="space-between"
        sx={{ mb: 0.5 }}
      >
        {label && (
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "text.primary" }}>
            {label}
          </Typography>
        )}
        <Stack direction="row" spacing={1} alignItems="baseline">
          {detail && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {detail}
            </Typography>
          )}
          {valueText && (
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, minWidth: 36, textAlign: "right" }}
            >
              {valueText}
            </Typography>
          )}
        </Stack>
      </Stack>
    )}
    <LinearProgress
      variant="determinate"
      value={clampPercent(percent)}
      sx={{
        height: 6,
        borderRadius: 3,
        bgcolor: "action.selected",
        "& .MuiLinearProgress-bar": { bgcolor: color },
      }}
    />
  </Box>
);

const BandBreakdown = ({ bands, footer }) => (
  <Stack spacing={1}>
    {bands.map(({ id, label, color, stats, bars }) => (
      <Card key={id} variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                bgcolor: color,
                flexShrink: 0,
              }}
            />
            <Typography variant="subtitle2">{label}</Typography>
          </Stack>
          {stats?.length > 0 && (
            <Stack spacing={0.25} sx={{ mb: bars?.length ? 1 : 0 }}>
              {stats.map(({ label: statLabel, value }) => (
                <Stack key={statLabel} direction="row" justifyContent="space-between">
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {statLabel}
                  </Typography>
                  <Typography variant="body2">{value ?? "—"}</Typography>
                </Stack>
              ))}
            </Stack>
          )}
          {bars?.length > 0 && (
            <Stack spacing={1.25}>
              {bars.map((bar) => (
                <BandBar key={bar.id} {...bar} />
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>
    ))}
    {footer?.length > 0 && (
      <Stack direction="row" spacing={3} sx={{ pt: 0.5, flexWrap: "wrap" }}>
        {footer.map(({ label, value }) => (
          <Box key={label}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {label}
            </Typography>
            <Typography variant="subtitle2">{value ?? "—"}</Typography>
          </Box>
        ))}
      </Stack>
    )}
  </Stack>
);

export default BandBreakdown;
